// src/App.js
import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

import Homepage from './pages/Homepage';
import Contactpage from './pages/Contactpage';
import Pricingpage from './pages/Pricingpage';
import Servicepage from './pages/Servicepage';
import Aboutpage from './pages/Aboutpage';
import Privacypage from './pages/Privacypage';
import Termspage from './pages/Termspage';
import ThankYouPage from './components/ThankYouPage';

import { seoConfig, buildCanonical } from './seoConfig';
import Blogpage from './pages/Blogpage';
import Everettpage from './pages/Blogsdetails/Everettpage';
import Bellevuepage from './pages/Blogsdetails/Bellevuepage';
import Lynnwoodpage from './pages/Blogsdetails/Lynnwoodpage';
import Groutpage from './pages/Blogsdetails/Groutpage';
import Kirklandpage from './pages/Blogsdetails/Kirklandpage';
import Seattlepage from './pages/Blogsdetails/Seattlepage';
import Tacomapage from './pages/Blogsdetails/Tacomapage';
import Postcleaningpage from './pages/Blogsdetails/Postcleaningpage';

/* ===== GA4 (in code) ===== */
ReactGA.initialize('G-DSGZW551HQ');

/* ===== RouteMeta (React 19 hoisted head tags) ===== */
const DEFAULTS = {
  title: 'Greenworks Carpet Cleaning',
  description:
    'Eco-friendly carpet, upholstery, tile & grout cleaning across Lynnwood & nearby areas. Same-day service available. Call (206) 899-6862.',
  siteName: 'Greenworks Carpet Cleaning',
  keywords:
    'carpet cleaning, upholstery cleaning, rug cleaning, deep cleaning, eco-friendly cleaning, professional cleaners, Lynnwood cleaning service, Seattle area cleaning, stain removal, pet odor removal, green cleaning solutions, commercial cleaning, residential cleaning, steam cleaning, carpet shampooing, tile and grout cleaning',
};
const OG_IMAGE = `${process.env.PUBLIC_URL || ''}/green-logo.png`;

const RouteMeta = () => {
  const { pathname } = useLocation();
  const cfg = seoConfig[pathname] || {};
  const title = cfg.title || DEFAULTS.title;
  const description = cfg.description || DEFAULTS.description;
  const canonical = buildCanonical(pathname);

  // React 19 will hoist these into <head>
  return (
    <>
      {/* Primary */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={DEFAULTS.keywords} />

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={OG_IMAGE} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </>
  );
};

/* ===== Analytics on route change ===== */
const AnalyticsTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    const pagePath = location.pathname + location.search + location.hash;
    const pageTitle =
      seoConfig[location.pathname]?.title || document.title;
    ReactGA.send({ hitType: 'pageview', page: pagePath, title: pageTitle });
  }, [location]);
  return null;
};

/* ===== Scroll helpers ===== */
const ScrollToHashElement = () => {
  const location = useLocation();
  React.useEffect(() => {
    let timer;

    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        timer = window.setTimeout(
          () => el.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          200
        );
      }
    }

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [location]);
  return null;
};
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App = () => (
  <>
    {/* Per-route metadata */}
    <RouteMeta />

    {/* GA4 + UX helpers */}
    <AnalyticsTracker />
    <ScrollToTop />
    <ScrollToHashElement />

    <Routes>
      <Route path='/' element={<Homepage />} />
      <Route path='/contact' element={<Contactpage />} />
      <Route path='/pricing-details' element={<Pricingpage />} />
      <Route path='/services' element={<Servicepage />} />
      <Route path='/about' element={<Aboutpage />} />
      <Route path='/privacy' element={<Privacypage />} />
      <Route path='/terms' element={<Termspage />} />
      <Route path='/confirmation' element={<ThankYouPage />} />

      <Route path='/articles' element={<Blogpage />} />

      <Route path='/blog/everett-expert-residential-carpet-cleaners' element={<Everettpage />} />
      <Route path='/blog/bellevue-couch-cleaning-services' element={<Bellevuepage />} />
      <Route path='/blog/lynnwood-holiday-cleaning-services' element={<Lynnwoodpage />} />
      <Route path='/blog/lynnwood-grout-stains' element={<Groutpage />} />
      <Route path='/blog/kirkland-deep-cleaning-vs-spot-treatments' element={<Kirklandpage />} />
      <Route path='/blog/seattle-holiday-carpet-cleaning-early' element={<Seattlepage />} />
      <Route path='/blog/tacoma-skip-annual-hardwood-cleaning' element={<Tacomapage />} />
      <Route path='/blog/bellevue-carpet-looks-worn-stretching-helps' element={<Postcleaningpage/>} />


    </Routes>
  </>
);

export default App;
